<template>
    <ui-container class="py-8">
        <ui-header :title="editMode ? 'Editing Route Plan - #'+this.item.id : 'Create new Route Plan'" :subtitle="' '">
            <template #actions>
                <ui-button-new icon="fas fa-arrow-left" @click="finished()" v-if="editMode">Done</ui-button-new>
            </template>
        </ui-header>
        <div class="grid gap-6 grid-cols-12 py-6">
            <ui-date-picker label="Route Plan Date" name="date" v-model="form.date" class="xl:col-span-12"
                            placeholder="What is the planned date for this route?"
                            :error="form.errors.date"
                            @input="save"></ui-date-picker>
            <ui-input label="Vehicle Registration" name="vehicleRegistration" v-model="form.vehicleRegistration"
                      class="xl:col-span-6" @input="save"></ui-input>
            <ui-input label="Driver" name="driver" v-model="form.driver"
                      class="xl:col-span-6" @input="save"></ui-input>
        </div>
        <label class="my-0 py-0 mt-0">Jobs</label>
        <div
            class="flex mt-1.5 border-t border-b border-gray-200 bg-gray-50 text-xs text-gray-500 font-semibold" v-if="!singleColumn">
            <div class="w-6 flex-shrink-0"></div>
            <div class="flex-1 border-l border-r border-gray-200 px-2 py-1">Address</div>
            <div class="flex-1 border-r border-gray-200 px-2 py-1">Contact</div>
            <div class="flex-1 border-r border-gray-200 px-2 py-1">Materials</div>
            <div class="w-6 flex-shrink-0 ml-2">
            </div>
        </div>
        <draggable :list="currentJobs" draggable=".draggable" handle=".handle" group="jobs" @change="save">
            <route-plans-job-item v-for="(job,index) in currentJobs"
                                  :key="job.id"
                                  :job="job"
                                  :clipboard="clipboard"
                                  ref="jobs"
                                  :single-column="singleColumn"
                                  @updatedJob="save"
                                  @cutJob="cutJob"
                                  @copyJob="copyJob"
                                  @pasteJobs="pasteJobs"
                                  @clearClipboard="clearClipboard"
                                  @removeJob="removeJob"
            ></route-plans-job-item>
        </draggable>
        <!-- New Task Item. -->
        <div class="flex border-b -mb-px border-gray-200">
            <div class="w-6 flex-shrink-0">
                <div class="px-2 py-2 w-5 h-5 flex jobs-center">
                    <i class="fa fa-plus text-xs text-gray-300"></i>
                </div>
            </div>
            <div class="flex-1 border-l border-r border-gray-200 px-0 py-0">
                <textarea
                    placeholder="Add a new job..."
                    class="px-2 py-1 b-0 border-0 hover:z-10 focus:z-20 resize-none text-sm m-0 border-none shadow-none relative"
                    type="text"
                    rows="1"
                    v-model="newJob.address"
                    @input="addJobLine"
                ></textarea>
            </div>
            <div class="w-6 ml-2 mt-1">
                <ui-dropdown menu-class="p-3 w-56" v-if="clipboard.length">
                    <template #default="{ toggle }">
                        <button @click="toggle"
                                class="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                            <i class="fa fa-ellipsis-h fa-fw"></i>
                        </button>
                    </template>
                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-paste" @click="pasteJobs(-1);close();" v-if="clipboard.length">
                            {{ 'Paste (' + clipboard.length + ' jobs)' }}
                        </ui-dropdown-item>
                        <hr v-if="clipboard.length" class="m-1.5"/>
                        <ui-dropdown-item icon="fas fa-clipboard" @click="clearClipboard();close();" v-if="clipboard.length">
                            {{ 'Clear Clipboard (' + clipboard.length + ' jobs)' }}
                        </ui-dropdown-item>
                    </template>
                </ui-dropdown>
            </div>
        </div>

        <ui-data-grid-item wide class="mt-6 flex" v-if="!editMode">
            <div class="flex flex-col md:flex-row justify-end flex-wrap">
                <ui-button-new @click="clear" class="ml-3">Clear</ui-button-new>
                <ui-button-new primary @click.prevent="form.submit()" :loading="form.busy" class="ml-3">Create
                </ui-button-new>
            </div>
        </ui-data-grid-item>

        <ui-data-grid v-else class="mt-6 flex">
            <ui-data-grid-item label="Created By">{{ item.createdBy }} on {{ item.createdOn }}</ui-data-grid-item>
            <ui-data-grid-item label="Last Updated By" v-if="item.lastUpdatedBy">{{ item.lastUpdatedBy }} on {{
                    item.lastUpdatedOn
                }}
            </ui-data-grid-item>
        </ui-data-grid>

    </ui-container>

</template>

<script>
import {RoutePlan} from '@models'
import UiInput from "../../ui/UiInput.vue";
import RoutePlansJobItem from "./RoutePlansJobItem.vue";
import draggable from 'vuedraggable'
import UiContainer from "../../ui/UiContainer.vue";
import UiDatePicker from "../../ui/UiDatePicker.vue";
import UiButtonNew from "../../ui/UiButtonNew.vue";
import AutoresizableTextarea from "../../ui/AutoresizableTextarea";
import UiDataGrid from "../../ui/UiDataGrid.vue";
import UiDataGridItem from "../../ui/UiDataGridItem.vue";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import UiDropdown from "../../ui/UiDropdown.vue";
import {cloneDeep} from "lodash";
import {uuid} from "../../../utils";

export default {
    components: {
        UiDropdown, UiDropdownItem,
        UiDataGridItem,
        UiDataGrid,
        AutoresizableTextarea, UiButtonNew, UiDatePicker, UiContainer, RoutePlansJobItem, draggable, UiInput
    },
    props: {
        item: Object,
        edit: Function,
        addItem: Function,
        jobs: Array,
        singleColumn:false,
    },
    data() {
        let draft = localStorage.getItem('route-plan-draft');
        draft = draft ? JSON.parse(draft) : {jobs: []};
        const initialData = this.item ? this.item.getFormAttributes() : draft;
        const clipboardData = localStorage.getItem('route-plan-clipboard');
        return {
            currentJobs: this.jobs ? this.jobs.slice(0) : (initialData.jobs ? initialData.jobs : []),
            clipboard: clipboardData ? JSON.parse(clipboardData) : [],
            newJob: {
                address: null,
            },
            form: new Form(initialData, async (form) => {
                let data = form.export();
                if (!data.date) {
                    form.errors.date = "The date field is required."
                    return;
                }
                data.jobs = this.currentJobs ? this.currentJobs : [];
                let sorter = (items) => {
                    items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
                };

                if (this.editMode) {
                    this.item.update(data).catch(error => {
                        // Do something when errored
                    }).then((value) => {
                        if (value) {
                            value.jobs.forEach((v, i) => {
                                this.currentJobs[i].id = v.id;
                            });
                            this.edit(true, sorter)
                        }
                    })
                } else {
                    const routePlan = await RoutePlan.create(data)

                    this.addItem(routePlan, 0, sorter);
                    this.clear();
                    form.reset()
                }
            }),
        }
    },
    methods: {
        clear() {
            this.form.date = '';
            this.form.registration = '';
            this.currentJobs = [];
            localStorage.setItem('route-plan-draft', false);
        },
        addJobLine() {
            if (!this.newJob.address) return
            this.currentJobs.push({
                id: uuid(),
                address: this.newJob.address,
                contact: '',
                materials: '',
                status: 'Awaiting'
            });
            this.save();
            this.newJob.address = ''
            this.$nextTick(() => {
                const inputField = this.$refs.jobs[this.$refs.jobs.length - 1].$refs.address
                let val = inputField.value;
                inputField.focus();
                inputField.value = val;
            })
        },
        removeJob(e) {
            this.currentJobs = this.currentJobs.filter(job => job.id !== e.id);
            this.save();
        },
        cutJob(e) {
            this.clipboard.push(e);
            localStorage.setItem('route-plan-clipboard', JSON.stringify(this.clipboard));
            this.removeJob(e);
        },
        copyJob(e) {
            let job = cloneDeep(e);
            this.clipboard.push(job);
            localStorage.setItem('route-plan-clipboard', JSON.stringify(this.clipboard));
        },
        pasteJobs(e) {
            this.clipboard.forEach((job) => {
                job.id = uuid()
                job.status = 'Awaiting'
            });
            if (e === -1) {
                this.currentJobs.push(...this.clipboard);
            } else {
                let index = this.currentJobs.findIndex((job) => job === e);
                this.currentJobs.splice(index + 1, 0, ...this.clipboard);
            }
            this.clipboard = [];
            localStorage.setItem('route-plan-clipboard', JSON.stringify(this.clipboard));
            this.save();
        },
        clearClipboard() {
            this.clipboard = [];
            localStorage.setItem('route-plan-clipboard', JSON.stringify(this.clipboard));
        },
        save() {
            if (this.editMode) {
                this.form.submit()
            } else {
                let data = this.form.export();
                data.jobs = this.currentJobs;
                localStorage.setItem('route-plan-draft', JSON.stringify(data));
            }
        },
        finished() {
            let sorter = (items) => {
                items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
            };
            this.edit(false, sorter)
        }
    },
    computed: {
        editMode() {
            return !!this.item
        },
    },
    beforeDestroy() {
        if (this.item) {
            this.item.releaseEditLock()
        }
    }
}
</script>
